import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, FilePen, Award, PartyPopper, AlertTriangle, Save, Plus, Copy, Trash2, Eye, RefreshCw, Car, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface DocumentTemplate {
  id: string;
  template_type: string;
  employment_type: string | null;
  name: string;
  description: string | null;
  content: unknown;
  placeholders: string[];
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

const templateTypeLabels: Record<string, string> = {
  employment_contract: "Arbeitsvertrag",
  contract_amendment: "Vertragsänderung",
  certificate: "Zertifikat",
  anniversary: "Jubiläumsurkunde",
  warning: "Abmahnung",
  vehicle_assignment: "Fahrzeug",
  safety_instruction: "Sicherheit"
};

const employmentTypeLabels: Record<string, string> = {
  vollzeit: "Vollzeit",
  teilzeit: "Teilzeit",
  minijob: "Minijob",
  azubi: "Auszubildende",
  werkstudent: "Werkstudent"
};

const templateTypeIcons: Record<string, React.ReactNode> = {
  employment_contract: <FileText className="h-4 w-4" />,
  contract_amendment: <FilePen className="h-4 w-4" />,
  certificate: <Award className="h-4 w-4" />,
  anniversary: <PartyPopper className="h-4 w-4" />,
  warning: <AlertTriangle className="h-4 w-4" />,
  vehicle_assignment: <Car className="h-4 w-4" />,
  safety_instruction: <ShieldCheck className="h-4 w-4" />
};

export const DocumentTemplateEditor = () => {
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("employment_contract");
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);
  const [editedContent, setEditedContent] = useState<Record<string, string>>({});
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedEmploymentType, setSelectedEmploymentType] = useState<string>("vollzeit");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from("document_templates")
        .select("*")
        .order("template_type")
        .order("sort_order");

      if (error) throw error;
      
      // Parse placeholders from JSON
      const parsedTemplates = (data || []).map(t => ({
        ...t,
        placeholders: Array.isArray(t.placeholders) ? t.placeholders : JSON.parse(t.placeholders as string || '[]')
      }));
      
      setTemplates(parsedTemplates);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Fehler beim Laden der Vorlagen");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template: DocumentTemplate) => {
    setSelectedTemplate(template);
    setEditedName(template.name);
    setEditedDescription(template.description || "");
    
    // Flatten content sections for editing
    const content = template.content as { sections?: Record<string, string> };
    const sections = content.sections || {};
    setEditedContent(sections);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nicht angemeldet");

      const updatedContent = {
        ...(selectedTemplate.content as object),
        sections: editedContent
      };

      const { error } = await supabase
        .from("document_templates")
        .update({
          name: editedName,
          description: editedDescription,
          content: updatedContent
        })
        .eq("id", selectedTemplate.id);

      if (error) throw error;

      toast.success("Vorlage gespeichert");
      fetchTemplates();
      
      // Update selected template
      setSelectedTemplate({
        ...selectedTemplate,
        name: editedName,
        description: editedDescription,
        content: updatedContent
      });
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Fehler beim Speichern der Vorlage");
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateTemplate = async () => {
    if (!selectedTemplate || !newTemplateName.trim()) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nicht angemeldet");

      const { data, error } = await supabase
        .from("document_templates")
        .insert([{
          template_type: selectedTemplate.template_type,
          employment_type: selectedTemplate.employment_type,
          name: newTemplateName.trim(),
          description: selectedTemplate.description,
          content: selectedTemplate.content as Json,
          placeholders: selectedTemplate.placeholders as Json,
          is_default: false,
          is_active: true,
          sort_order: templates.filter(t => t.template_type === selectedTemplate.template_type).length,
          created_by: user.id
        }])
        .select()
        .single();

      if (error) throw error;

      toast.success("Vorlage dupliziert");
      setCreateDialogOpen(false);
      setNewTemplateName("");
      
      // Refresh templates and select the new one
      await fetchTemplates();
      if (data) {
        const newTemplate = {
          ...data,
          placeholders: Array.isArray(data.placeholders) ? data.placeholders : JSON.parse(data.placeholders as string || '[]')
        };
        handleSelectTemplate(newTemplate);
      }
    } catch (error) {
      console.error("Error duplicating template:", error);
      toast.error("Fehler beim Duplizieren der Vorlage");
    }
  };

  const handleCreateNewTemplate = async () => {
    if (!newTemplateName.trim()) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Nicht angemeldet");

      // Find an existing template to use as base
      const baseTemplate = templates.find(t => 
        t.template_type === activeTab && 
        (activeTab !== "employment_contract" || t.employment_type === selectedEmploymentType)
      );

      const defaultContent = baseTemplate?.content || {
        title: "Neue Vorlage",
        subtitle: "",
        sections: {
          inhalt: "Hier können Sie den Inhalt der Vorlage eingeben..."
        }
      };

      const { data, error } = await supabase
        .from("document_templates")
        .insert([{
          template_type: activeTab,
          employment_type: activeTab === "employment_contract" ? selectedEmploymentType : null,
          name: newTemplateName.trim(),
          description: "",
          content: defaultContent as Json,
          placeholders: (baseTemplate?.placeholders || ["mitarbeiter_name", "position", "datum"]) as Json,
          is_default: false,
          is_active: true,
          sort_order: templates.filter(t => t.template_type === activeTab).length,
          created_by: user.id
        }])
        .select()
        .single();

      if (error) throw error;

      toast.success("Neue Vorlage erstellt");
      setCreateDialogOpen(false);
      setNewTemplateName("");
      
      // Refresh templates and select the new one
      await fetchTemplates();
      if (data) {
        const newTemplate = {
          ...data,
          placeholders: Array.isArray(data.placeholders) ? data.placeholders : JSON.parse(data.placeholders as string || '[]')
        };
        handleSelectTemplate(newTemplate);
      }
    } catch (error) {
      console.error("Error creating template:", error);
      toast.error("Fehler beim Erstellen der Vorlage");
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      const { error } = await supabase
        .from("document_templates")
        .delete()
        .eq("id", templateId);

      if (error) throw error;

      toast.success("Vorlage gelöscht");
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Fehler beim Löschen der Vorlage");
    }
  };

  const insertPlaceholder = (placeholder: string, sectionKey: string) => {
    const currentValue = editedContent[sectionKey] || "";
    setEditedContent({
      ...editedContent,
      [sectionKey]: currentValue + `{{${placeholder}}}`
    });
  };

  const filteredTemplates = templates.filter(t => t.template_type === activeTab);

  if (loading) {
    return <div className="flex justify-center p-8">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Dokumentvorlagen bearbeiten
          </CardTitle>
          <CardDescription>
            Bearbeiten Sie die Texte für Arbeitsverträge, Vertragsänderungen, Zertifikate, Jubiläumsurkunden und Abmahnungen.
            Verwenden Sie Platzhalter wie {"{{mitarbeiter_name}}"} für automatische Ersetzung.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); setSelectedTemplate(null); }}>
            <TabsList className="grid w-full grid-cols-3 lg:grid-cols-7 h-auto gap-1 mb-6">
              {Object.entries(templateTypeLabels).map(([key, label]) => (
                <TabsTrigger key={key} value={key} className="flex items-center gap-2 text-xs sm:text-sm py-2">
                  {templateTypeIcons[key]}
                  <span className="hidden sm:inline">{label}</span>
                  <span className="sm:hidden">{label.substring(0, 6)}.</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.keys(templateTypeLabels).map(templateType => (
              <TabsContent key={templateType} value={templateType} className="space-y-4">
                <div className="flex flex-wrap gap-4 items-start">
                  {/* Template List */}
                  <div className="w-full md:w-64 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-medium">Vorlagen</Label>
                      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Plus className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Neue Vorlage erstellen</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label>Name der Vorlage</Label>
                              <Input
                                value={newTemplateName}
                                onChange={(e) => setNewTemplateName(e.target.value)}
                                placeholder="z.B. Arbeitsvertrag Spezial"
                              />
                            </div>
                            {templateType === "employment_contract" && (
                              <div className="space-y-2">
                                <Label>Beschäftigungsart</Label>
                                <Select value={selectedEmploymentType} onValueChange={setSelectedEmploymentType}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Object.entries(employmentTypeLabels).map(([key, label]) => (
                                      <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                          <DialogFooter>
                            <DialogClose asChild>
                              <Button variant="outline">Abbrechen</Button>
                            </DialogClose>
                            <Button onClick={handleCreateNewTemplate} disabled={!newTemplateName.trim()}>
                              Erstellen
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <ScrollArea className="h-[400px] border rounded-md p-2">
                      <div className="space-y-1">
                        {filteredTemplates.map(template => (
                          <Button
                            key={template.id}
                            variant={selectedTemplate?.id === template.id ? "secondary" : "ghost"}
                            className="w-full justify-start text-left h-auto py-2"
                            onClick={() => handleSelectTemplate(template)}
                          >
                            <div className="flex flex-col items-start gap-1">
                              <span className="font-medium text-sm">{template.name}</span>
                              <div className="flex gap-1">
                                {template.employment_type && (
                                  <Badge variant="outline" className="text-xs">
                                    {employmentTypeLabels[template.employment_type] || template.employment_type}
                                  </Badge>
                                )}
                                {template.is_default && (
                                  <Badge variant="secondary" className="text-xs">Standard</Badge>
                                )}
                              </div>
                            </div>
                          </Button>
                        ))}
                        {filteredTemplates.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            Keine Vorlagen vorhanden
                          </p>
                        )}
                      </div>
                    </ScrollArea>
                  </div>

                  {/* Template Editor */}
                  <div className="flex-1 min-w-0">
                    {selectedTemplate ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex-1 min-w-[200px] space-y-2">
                            <Label>Name</Label>
                            <Input
                              value={editedName}
                              onChange={(e) => setEditedName(e.target.value)}
                            />
                          </div>
                          <div className="flex gap-2 mt-6">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setPreviewOpen(true)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Vorschau
                            </Button>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="outline" size="sm">
                                  <Copy className="h-4 w-4 mr-1" />
                                  Duplizieren
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Vorlage duplizieren</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="space-y-2">
                                    <Label>Name der neuen Vorlage</Label>
                                    <Input
                                      value={newTemplateName}
                                      onChange={(e) => setNewTemplateName(e.target.value)}
                                      placeholder="z.B. Arbeitsvertrag Kopie"
                                    />
                                  </div>
                                </div>
                                <DialogFooter>
                                  <DialogClose asChild>
                                    <Button variant="outline">Abbrechen</Button>
                                  </DialogClose>
                                  <Button onClick={handleDuplicateTemplate} disabled={!newTemplateName.trim()}>
                                    Duplizieren
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                            {!selectedTemplate.is_default && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="outline" size="sm" className="text-destructive">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Vorlage löschen?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Diese Aktion kann nicht rückgängig gemacht werden.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => handleDeleteTemplate(selectedTemplate.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Löschen
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Beschreibung</Label>
                          <Input
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            placeholder="Kurze Beschreibung der Vorlage"
                          />
                        </div>

                        {/* Placeholders */}
                        <div className="space-y-2">
                          <Label>Verfügbare Platzhalter (klicken zum Einfügen)</Label>
                          <div className="flex flex-wrap gap-1 p-2 border rounded-md bg-muted/50 max-h-24 overflow-y-auto">
                            {selectedTemplate.placeholders.map(placeholder => (
                              <Badge 
                                key={placeholder} 
                                variant="secondary" 
                                className="cursor-pointer hover:bg-primary hover:text-primary-foreground text-xs"
                                onClick={() => {
                                  // Copy to clipboard
                                  navigator.clipboard.writeText(`{{${placeholder}}}`);
                                  toast.success(`{{${placeholder}}} kopiert`);
                                }}
                              >
                                {`{{${placeholder}}}`}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {/* Content Sections */}
                        <div className="space-y-4">
                          <Label className="font-medium">Dokumentinhalt</Label>
                          <ScrollArea className="h-[400px] border rounded-md p-4">
                            <div className="space-y-4">
                              {Object.entries(editedContent).map(([sectionKey, sectionValue]) => (
                                <div key={sectionKey} className="space-y-2">
                                  <Label className="text-sm font-medium capitalize">
                                    {sectionKey.replace(/_/g, " ")}
                                  </Label>
                                  <Textarea
                                    value={sectionValue}
                                    onChange={(e) => setEditedContent({
                                      ...editedContent,
                                      [sectionKey]: e.target.value
                                    })}
                                    rows={Math.min(15, Math.max(4, sectionValue.split("\n").length + 1))}
                                    className="font-mono text-sm"
                                  />
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </div>

                        <div className="flex justify-end">
                          <Button onClick={handleSaveTemplate} disabled={saving}>
                            <Save className="h-4 w-4 mr-2" />
                            {saving ? "Speichern..." : "Änderungen speichern"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-[400px] border rounded-md bg-muted/20">
                        <p className="text-muted-foreground">
                          Wählen Sie eine Vorlage aus der Liste aus, um sie zu bearbeiten.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vorschau: {selectedTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="bg-white text-black p-8 rounded-md border shadow-inner font-serif">
            {selectedTemplate && (
              <div className="space-y-6">
                <div className="text-center space-y-2">
                  <h1 className="text-2xl font-bold">
                    {(selectedTemplate.content as { title?: string }).title || selectedTemplate.name}
                  </h1>
                  {(selectedTemplate.content as { subtitle?: string }).subtitle && (
                    <p className="text-lg text-gray-600">
                      {(selectedTemplate.content as { subtitle?: string }).subtitle}
                    </p>
                  )}
                </div>
                {Object.entries(editedContent).map(([key, value]) => (
                  <div key={key} className="whitespace-pre-wrap text-sm leading-relaxed">
                    {value.split(/(\{\{[^}]+\}\})/g).map((part, i) => 
                      part.startsWith("{{") ? (
                        <span key={i} className="bg-yellow-200 px-1 rounded font-mono text-xs">
                          {part}
                        </span>
                      ) : (
                        <span key={i}>{part}</span>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
